import Vue from 'vue';
import NotifyScroll from '@/Utils/NotifyScroll';
import FilterQueryHelper from '@/Mixins/FilterQueryHelpers.js';
import ToggleTrigger from '@/Components/ToggleTrigger';
import ToggleContainer from '@/Components/ToggleContainer';
import DropdownGroup from '@/Components/DropdownGroup.vue';
import DropdownLink from '@/Components/DropdownLink.vue';
import DropdownRouterLink from '@/Components/DropdownRouterLink.vue';
import AdminComponentBase from '@/Components/Admin/AdminComponentBase';
import LoadMoreTrigger from '@/Components/Admin/LoadMoreTrigger.vue';
import Grid from '@/Components/Admin/Grid';
import { injectTypes } from '@/Types/InjectTypes';

/** *
 * Grid component settings.
 */
export class GridComponentSettings {
    /** *
     * Controls row selection mode. If set to true, selecting all rows will actually attempt to load the underlying entity IDs.
     * Explicit row selection is a legacy mode that shouldn't be used in new components.
     * @type {boolean}
     */
    explicitRowSelectionEnabled = true;

    /** *
     * If true, grid won't load data on mount.
     * Data loads only after user applies filters/search.
     * Useful for expensive queries that need filtering.
     * @type {boolean}
     */
    lazyLoadEnabled = false;

    /** *
     * Row grouping settings.
     */
    grouping = {
        /** *
         * Set to true if grid rows should be grouped. Configure other fields on this object to control how the grouping works.
         * @type {boolean}
         */
        enabled: false,
        /** *
         * @type {(function({Object}): {Object})}
         */
        groupSelector: null,

        /** *
         * @type {(function(*): number)}
         */
        groupIdentitySelector: null,

        /** *
         * @type {(function(*, *): number)}
         */
        groupSortingComparer: null,
    };
}

export default {
    extends: AdminComponentBase,
    mixins: [FilterQueryHelper],
    provide() {
        return {
            [injectTypes.applyFilters]: this.applyFilters,
        };
    },
    components: {
        'filters': Grid.FiltersBox,
        'text-filter': Grid.TextFilter,
        'option-filter': Grid.OptionFilter,
        'pager': Grid.Pager,
        'date-range-filter': Grid.DateRangeFilter,
        'conditional-date-range-filter': Grid.ConditionalDateRangeFilter,
        'numeric-range-filter': Grid.NumericRangeFilter,
        'auto-complete-multi-select-filter': Grid.AutoCompleteMultiSelectFilter,
        'multi-select-filter': Grid.MultiSelectFilter,
        'auto-complete-filter': Grid.AutoCompleteFilter,
        NotifyScroll,
        DropdownGroup,
        DropdownLink,
        DropdownRouterLink,
        ToggleContainer,
        ToggleTrigger,
        LoadMoreTrigger,
    },
    data() {
        return {
            pagedItems: [],
            groupedPagedItems: [],
            expandedRows: [],
            totalItemCount: 0,
            pageIndex: 0,
            pageSize: 100,
            selectedRowIDs: [],
            allRowsSelectedFlag: false,
            editRow: this.getDefaultEditRow(),
            editGroupRow: this.getDefaultEditGroupRow(),
            isInitialized: false,
            isEditing: false,
            isCreating: false,
            isEditingGroup: false,
            isCreatingGroup: false,
            groupingConfig: null,
            creatingPosition: null,
            draggedRow: null,
        };
    },
    methods: {
        applyFilters() {
            this.pageIndex = 0;
            this.clearSelection();

            if (this.$refs.rowsContainer) {
                this.$refs.rowsContainer.scrollTop = 0;
            }
            this.updateFiltersQuery(this.selectFilters);
            return this.loadRows();
        },
        /** *
         * When overriden in a derived component, configures the component behavior.
         * @param settings {GridComponentSettings}
         */
        configure(settings) {
        },
        getDefaultEditRow() {
            return {};
        },
        getItemID(item) {
            throw 'getItemID is not implemented for Grid component.';
        },
        isRowSelected(rowID) {
            if (this.settings.explicitRowSelectionEnabled && this.allRowsSelected)
                return true;

            return this.selectedRowIDs.includes(rowID);
        },
        getDefaultEditGroupRow() {
            return {};
        },
        initialize() {
            this.$validator.pause();

            return this.onInitialized();
        },
        onInitialized() {
            if (this.isAwaitingFirstLoad && this.hasFilterParamsInUrl()) {
                return this.loadRows();
            }

            if (this.settings.lazyLoadEnabled) {
                return Promise.resolve();
            }

            return this.loadRows();
        },
        hasFilterParamsInUrl() {
            return Object.keys(this.$route.query).some(key => key.startsWith('filter-'));
        },
        addResultsLoadedParamToUrl() {
            if (this.settings.lazyLoadEnabled && !this.$route.query['filter-results']) {
                const newQuery = {
                    ...this.$route.query,
                    'filter-results': '1',
                };
                this.$router.replace({
                    path: this.$route.path,
                    query: newQuery,
                }, () => {});
            }
        },
        groupPageItems() {
            if (this.settings.grouping.enabled) {
                const result = [];
                this.pagedItems.forEach(item => {
                    const group = this.settings.grouping.groupSelector(item);
                    const id = this.settings.grouping.groupIdentitySelector(group);
                    let groupItem = result.find(r => this.settings.grouping.groupIdentitySelector(r.group) === id);
                    if (!groupItem) {
                        groupItem = { group, items: [] };
                        result.push(groupItem);
                    }
                    groupItem.items.push(item);
                });

                if (this.settings.grouping.groupSortingComparer) {
                    result.sort((a, b) => this.settings.grouping.groupSortingComparer(a.group, b.group));
                }
                this.groupedPagedItems = result;
            }
            else {
                this.groupedPagedItems = [];
            }
        },
        loadRows() {
            this.pageIndex = 0;
            if (this.isEditingGroup) {
                this.stopEditingGroup();
            }

            return this.wrapAsyncTask(this.loadRowsPromise());
        },
        loadRowsPromise() {
            return this.getRowsPromise()
                .then((response) => {
                    this.pagedItems = response.data.pagedItems || response.data;
                    this.totalItemCount = response.data.totalItemCount || 0;
                    this.groupPageItems();
                    return response;
                })
                .then(this.onLoadRowsSuccess)
                .finally(() => {
                    this.isInitialized = true;
                    this.addResultsLoadedParamToUrl();
                });
        },
        loadMoreRows() {
            if (!this.canLoadMoreRows)
                return;

            this.pushProcessing();
            this.pageIndex++;

            this.loadMoreRowsPromise()
                .then(this.popProcessing);
        },
        loadMoreRowsPromise() {
            return this.getRowsPromise()
                .then((response) => {
                    this.pagedItems = this.pagedItems.concat(response.data.pagedItems || response.data);
                    this.totalItemCount = response.data.totalItemCount || 0;

                    if (!this.settings.explicitRowSelectionEnabled && this.allRowsSelectedFlag) {
                        this.selectedRowIDs = this.selectedRowIDs.concat(this.pagedItems.map(i => this.getItemID(i)));
                    }

                    return response;
                })
                .then(this.onLoadRowsSuccess)
                .catch(this.handleError);
        },
        loadAllRowIDs() {
            this.pushProcessing();

            return this.loadAllRowIDsPromise()
                .then((response) => {
                    this.selectedRowIDs = response.data;
                })
                .catch(this.handleError)
                .then(this.popProcessing);
        },
        onCreatingSuccess() {
            // by default do nothing, it could be overriden by other components
        },
        onLoadRowsSuccess() {
            // by default do nothing, it could be overriden by other components
        },
        resetFilters() {
            this.resetFiltersToDefaultValues();
            this.expandedRows = [];
        },
        startEditing(row) {
            if (!this.groupRowIsEdited && !this.rowIsEdited) {
                Object.assign(this.editRow, row);
                this.isEditing = true;
                this.focusCell();
            }
        },
        startEditingGroup(row) {
            if (!this.groupRowIsEdited && !this.rowIsEdited) {
                Object.assign(this.editGroupRow, row);
                this.isEditingGroup = true;
                this.focusCell();
            }
        },
        focusCell() {
            Vue.nextTick(() => {
                const editFocus = Array.isArray(this.$refs.editFocus) ? this.$refs.editFocus[0] : this.$refs.editFocus;
                const editFocus2 = Array.isArray(this.$refs.editFocus2) ? this.$refs.editFocus2[0] : this.$refs.editFocus2;
                if (editFocus && !editFocus.readOnly && typeof editFocus.focus === 'function') {
                    editFocus.focus();
                }
                else if (editFocus2 && !editFocus2.readOnly && typeof editFocus2.focus === 'function') {
                    editFocus2.focus();
                }
            });
        },
        submitEditing() {
            if (this.isCreating) {
                return this.submitCreating();
            }

            this.$validator.resume();

            this.$validator.validateAll().then(result => {
                if (result) {
                    this.saveEditedRowPromise().then(() => {
                        this.stopEditing();
                    });
                }
            });
        },
        submitEditingGroup() {
            if (this.isCreatingGroup) {
                return this.submitCreatingGroup();
            }

            this.$validator.resume();

            this.$validator.validateAll().then(result => {
                if (result) {
                    this.saveEditedGroupRowPromise().then(() => {
                        this.stopEditingGroup();
                    });
                }
            });
        },
        stopEditing() {
            if (this.isCreating) {
                return this.stopCreating();
            }

            this.isEditing = false;
            this.editRow = this.getDefaultEditRow();
            this.$validator.reset();
        },
        stopEditingGroup() {
            if (this.isCreatingGroup) {
                return this.stopCreatingGroup();
            }

            this.isEditingGroup = false;
            this.editGroupRow = this.getDefaultEditGroupRow();
            this.$validator.reset();
        },
        startCreating(groupedRow) {
            if (!this.groupRowIsEdited && !this.rowIsEdited) {
                Object.assign(this.editRow, this.getDefaultEditRow(groupedRow));
                this.isCreating = true;
                this.focusCell();
                if (this.settings.grouping.enabled) {
                    this.expandGroupRow(groupedRow);
                }
            }
        },
        startCreatingGroup() {
            if (!this.groupRowIsEdited && !this.rowIsEdited) {
                Object.assign(this.editGroupRow, this.getDefaultEditGroupRow());
                this.isCreatingGroup = true;
                this.focusCell();
            }
        },
        startCreatingPrefix() {
            this.creatingPosition = 'prefix';
            this.startCreating();
        },
        startCreatingSuffix() {
            this.creatingPosition = 'suffix';
            this.startCreating();
        },
        submitCreating() {
            this.$validator.resume();

            this.$validator.validateAll().then(result => {
                if (result) {
                    this.saveNewRowPromise()
                        .then(this.stopCreating)
                        .then(this.onCreatingSuccess);
                }
            });
        },
        submitCreatingGroup() {
            this.$validator.resume();

            this.$validator.validateAll().then(result => {
                if (result) {
                    this.saveNewGroupRowPromise()
                        .then(this.stopCreatingGroup);
                }
            });
        },
        stopCreating() {
            this.isCreating = false;
            this.editRow = this.getDefaultEditRow();
            this.$validator.reset();
        },
        stopCreatingGroup() {
            this.isCreatingGroup = false;
            this.editGroupRow = this.getDefaultEditGroupRow();
            this.$validator.reset();
        },
        expandedClass(groupedRow) {
            if (this.expandedRows.includes(this.settings.grouping.groupIdentitySelector(groupedRow.group))) {
                return '_fa _fa--chevron-down';
            }
            return '_fa _fa--chevron-right';
        },
        toggleGroupRow(groupedRow) {
            const id = this.settings.grouping.groupIdentitySelector(groupedRow.group);
            if (this.expandedRows.includes(id)) {
                for (let i = this.expandedRows.length; i--;) {
                    if (this.expandedRows[i] === id) {
                        this.expandedRows.splice(i, 1);
                    }
                }
            }
            else {
                this.expandedRows.push(id);
            }
        },
        expandGroupRow(groupedRow) {
            const id = this.settings.grouping.groupIdentitySelector(groupedRow.group);
            if (!this.expandedRows.includes(id)) {
                this.expandedRows.push(id);
            }
        },
        expandAllGroupRows() {
            this.expandedRows = this.groupedPagedItems.map(g => this.settings.grouping.groupIdentitySelector(g.group));
        },
        clearSelection() {
            this.selectedRowIDs = [];
            this.allRowsSelectedFlag = false;
        },
        toggleAllRowsSelection(isSelected) {
            if (isSelected) {
                if (this.settings.explicitRowSelectionEnabled) {
                    this.loadAllRowIDs().then(() => {
                        this.allRowsSelectedFlag = true;
                    });
                }
                else {
                    this.allRowsSelectedFlag = true;
                    this.selectedRowIDs = this.pagedItems.map(i => this.getItemID(i));
                }
            }
            else {
                this.selectedRowIDs = [];
                this.allRowsSelectedFlag = false;
            }
        },
    },
    watch: {
        selectedRowIDs(newValue, oldValue) {
            // if grid is in mode to load all rows IDs from server
            if (this.settings.explicitRowSelectionEnabled
                // and the flag is selected
                && this.allRowsSelectedFlag
                // and someone unchecks any row
                && newValue.length < oldValue.length) {
                // then unselect the flag
                this.allRowsSelectedFlag = false;
            }
        },
    },
    computed: {
        allRowsSelected: {
            get() {
                return this.allRowsSelectedFlag;
            },
            set(value) {
                this.toggleAllRowsSelection(value);
            },
        },
        canLoadMoreRows() {
            return !!(!this.isProcessing && this.totalItemCount && this.pagedItems.length < this.totalItemCount);
        },
        excludedRowIDs() {
            if (this.settings.explicitRowSelectionEnabled || !this.allRowsSelectedFlag)
                return [];

            return this.pagedItems.map(i => this.getItemID(i))
                .filter(i => !this.selectedRowIDs.includes(i));
        },
        groupRowIsEdited() {
            return this.isEditingGroup || this.isCreatingGroup;
        },
        rowIsEdited() {
            return this.isEditing || this.isCreating;
        },
        hasItems() {
            return this.pagedItemsCount > 0;
        },
        pagedItemsCount() {
            return this.pagedItems.length;
        },
        isAwaitingFirstLoad() {
            return this.settings.lazyLoadEnabled && !this.isInitialized;
        },
    },
    created() {
        this.settings = new GridComponentSettings();
        this.configure(this.settings);
    },
    mounted() {
        this.ensureStoreInitialized()
            .then(() => {
                this.$nextTick(() => {
                    this.initialize();
                });
            });
    },
    beforeRouteUpdate(to, from, next) {
        next();
    },
};
